import { useState, useEffect, useRef, useCallback } from 'react'
import { FiTrash2, FiUpload, FiRefreshCw, FiX } from 'react-icons/fi'
import { themes, uploads, type ThemeDetail, type ThemeImage } from '../api/client'
import type { ImageGenerationStatus } from '../api/client'
import { styles } from './adminStyles'

/* ── Theme Object View (full-page editor for a single ThemeImage) ── */

interface ThemeObjectViewProps {
  themeSlug: string
  imageId: number
  onBack: () => void
}

interface ObjectForm {
  wordEs:       string
  wordEn:       string
  altEs:        string
  altEn:        string
  definitionEs: string
  definitionEn: string
  value:        string
}

const objStyles: Record<string, React.CSSProperties> = {
  page: { minHeight: '100vh', display: 'flex', flexDirection: 'column', background: styles.root.background },
  header: {
    background: styles.header.background,
    padding: '16px 24px',
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    flexWrap: 'wrap',
  },
  backBtn: {
    fontSize: '0.9rem',
    fontWeight: 700,
    color: 'rgba(255,255,255,0.8)',
    background: 'rgba(255,255,255,0.12)',
    border: 'none',
    borderRadius: 10,
    padding: '8px 14px',
    cursor: 'pointer',
    minHeight: 40,
  },
  headerTitle: {
    fontFamily: "'Cormorant Garamond', serif",
    fontSize: '1.4rem',
    fontWeight: 700,
    color: '#FFFFFF',
    margin: 0,
  },
  headerSubtitle: {
    fontSize: '0.95rem',
    color: 'rgba(255,255,255,0.7)',
    fontWeight: 600,
  },
  content: {
    flex: 1,
    padding: '32px 24px',
    maxWidth: 700,
    width: '100%',
    margin: '0 auto',
    boxSizing: 'border-box',
  },
  previewCard: {
    background: '#FFFFFF',
    border: `1.5px solid ${styles.input.borderColor}`,
    borderRadius: 16,
    padding: '24px',
    marginBottom: 24,
    boxShadow: '0 2px 12px rgba(27,67,50,0.07)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 16,
  },
  previewImg: {
    maxWidth: '100%',
    maxHeight: 320,
    objectFit: 'contain',
    borderRadius: 12,
    background: '#fff',
  },
  formCard: {
    background: '#FFFFFF',
    border: `1.5px solid ${styles.input.borderColor}`,
    borderRadius: 16,
    padding: '24px',
    marginBottom: 24,
    boxShadow: '0 2px 12px rgba(27,67,50,0.07)',
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  formRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
    gap: 16,
  },
  formField: { display: 'flex', flexDirection: 'column', gap: 6 },
  label: { fontSize: '0.875rem', fontWeight: 700, color: '#6B4C3B', textTransform: 'uppercase', letterSpacing: '0.06em' },
  actionsCard: {
    background: '#FFFFFF',
    border: `1.5px solid ${styles.input.borderColor}`,
    borderRadius: 16,
    padding: '24px',
    marginBottom: 24,
    boxShadow: '0 2px 12px rgba(27,67,50,0.07)',
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  actionsTitle: {
    fontSize: '0.95rem',
    fontWeight: 800,
    color: styles.sectionTitle.color,
    margin: 0,
  },
  actionBtn: {
    fontSize: '0.88rem',
    fontWeight: 700,
    padding: '10px 18px',
    borderRadius: 10,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    border: 'none',
  },
  actionRow: {
    display: 'flex',
    gap: 10,
    flexWrap: 'wrap',
  },
  regenPanel: {
    background: '#FDFAF6',
    border: '1.5px solid #E8DDD0',
    borderRadius: 12,
    padding: '16px',
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  replacePanel: {
    background: '#FDFAF6',
    border: '1.5px solid #E8DDD0',
    borderRadius: 12,
    padding: '16px',
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  dangerBtn: {
    fontSize: '0.88rem',
    fontWeight: 700,
    color: '#FFFFFF',
    background: '#C0392B',
    padding: '10px 18px',
    borderRadius: 10,
    cursor: 'pointer',
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
}

export function ThemeObjectView({ themeSlug, imageId, onBack }: ThemeObjectViewProps) {
  const [theme, setTheme]         = useState<ThemeDetail | null>(null)
  const [image, setImage]         = useState<ThemeImage | null>(null)
  const [loading, setLoading]     = useState(true)
  const [form, setForm]           = useState<ObjectForm>({
    wordEs: '', wordEn: '', altEs: '', altEn: '', definitionEs: '', definitionEn: '', value: '',
  })
  const [saving, setSaving]       = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [saveSuccess, setSaveSuccess] = useState(false)

  // Regen state
  const [showRegen, setShowRegen]       = useState(false)
  const [regenType, setRegenType]       = useState<'texts' | 'definition' | 'image'>('image')
  const [regenPrompt, setRegenPrompt]   = useState('')
  const [regenSubmitting, setRegenSubmitting] = useState(false)
  const [regenError, setRegenError]     = useState<string | null>(null)

  // Replace state
  const [showReplace, setShowReplace]       = useState(false)
  const [replaceFile, setReplaceFile]       = useState<File | null>(null)
  const [replaceUploading, setReplaceUploading] = useState(false)
  const [replaceError, setReplaceError]     = useState<string | null>(null)
  const replaceFileRef = useRef<HTMLInputElement>(null)

  // Dismissed generation error — persisted to localStorage
  const [dismissedImageError, setDismissedImageError] = useState(() => {
    try { return localStorage.getItem(`dismissed-gen-error-image-${imageId}`) === 'true' } catch { return false }
  })

  const dismissImageError = useCallback(() => {
    try { localStorage.setItem(`dismissed-gen-error-image-${imageId}`, 'true') } catch { /* localStorage unavailable */ }
    setDismissedImageError(true)
  }, [imageId])

  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const loadData = useCallback(() => {
    themes.get(themeSlug)
      .then((t) => {
        setTheme(t)
        const img = t.images.find(i => i.id === imageId) ?? null
        setImage(img)
        if (img) {
          setForm({
            wordEs:       img.wordEs,
            wordEn:       img.wordEn,
            altEs:        img.altEs,
            altEn:        img.altEn,
            definitionEs: img.definitionEs ?? '',
            definitionEn: img.definitionEn ?? '',
            value:        img.value != null ? String(img.value) : '',
          })
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [themeSlug, imageId])

  useEffect(() => { loadData() }, [loadData])

  // Poll while image is PENDING or GENERATING
  useEffect(() => {
    const active = image?.generationStatus === 'PENDING' || image?.generationStatus === 'GENERATING'
    if (active && !pollRef.current) {
      pollRef.current = setInterval(loadData, 3000)
    } else if (!active && pollRef.current) {
      clearInterval(pollRef.current)
      pollRef.current = null
    }
    return () => {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null }
    }
  }, [image, loadData])

  const handleSave = async () => {
    if (!form.wordEs || !form.wordEn || !form.altEs || !form.altEn) {
      setSaveError('Nombre (ES/EN) y alt text (ES/EN) son obligatorios')
      return
    }
    setSaving(true)
    setSaveError(null)
    setSaveSuccess(false)
    try {
      const updated = await themes.updateImage(themeSlug, imageId, {
        wordEs:       form.wordEs,
        wordEn:       form.wordEn,
        altEs:        form.altEs,
        altEn:        form.altEn,
        definitionEs: form.definitionEs || undefined,
        definitionEn: form.definitionEn || undefined,
        value:        form.value !== '' ? parseInt(form.value, 10) : null,
      })
      setImage(updated)
      setSaveSuccess(true)
      setTimeout(() => setSaveSuccess(false), 2000)
    } catch (err: unknown) {
      setSaveError(err instanceof Error ? err.message : 'Error al guardar')
    } finally {
      setSaving(false)
    }
  }

  const handleSetCover = async () => {
    if (!image?.url) return
    try {
      await themes.update(themeSlug, { coverUrl: image.url })
      setTheme(prev => prev ? { ...prev, coverUrl: image.url } : prev)
    } catch { /* intentionally empty */ }
  }

  const handleDelete = async () => {
    if (!image) return
    if (!confirm(`¿Eliminar la imagen "${image.wordEs}"?`)) return
    try {
      await themes.deleteImage(themeSlug, image.id)
      onBack()
    } catch { /* intentionally empty */ }
  }

  const submitRegen = async () => {
    setRegenSubmitting(true)
    setRegenError(null)
    try {
      await themes.regenerateImage(themeSlug, imageId, {
        type: regenType,
        refinementPrompt: regenPrompt.trim() || undefined,
      })
      setTheme(prev => prev ? {
        ...prev,
        images: prev.images.map(i =>
          i.id === imageId ? { ...i, generationStatus: 'GENERATING' as ImageGenerationStatus } : i
        ),
      } : prev)
      setImage(prev => prev ? { ...prev, generationStatus: 'GENERATING' as ImageGenerationStatus } : prev)
      setShowRegen(false)
      setRegenPrompt('')
    } catch (err: unknown) {
      setRegenError(err instanceof Error ? err.message : 'Error al regenerar')
    } finally {
      setRegenSubmitting(false)
    }
  }

  const submitReplace = async () => {
    if (!replaceFile) { setReplaceError('Selecciona una imagen'); return }
    setReplaceUploading(true)
    setReplaceError(null)
    try {
      const { uploadUrl, publicUrl, key } = await uploads.presign(replaceFile.name, replaceFile.type as 'image/jpeg' | 'image/png' | 'image/webp' | 'image/gif', themeSlug)
      await uploads.putFile(uploadUrl, replaceFile)
      const updated = await themes.updateImage(themeSlug, imageId, { url: publicUrl, s3Key: key })
      setImage(updated)
      dismissImageError()
      setShowReplace(false)
      setReplaceFile(null)
    } catch (err: unknown) {
      setReplaceError(err instanceof Error ? err.message : 'Error al reemplazar la imagen')
    } finally {
      setReplaceUploading(false)
    }
  }

  if (loading) return (
    <div style={objStyles.page}>
      <header style={objStyles.header}>
        <button onClick={onBack} style={objStyles.backBtn}>← Temas</button>
      </header>
      <div style={objStyles.content}><p style={styles.emptyText}>Cargando…</p></div>
    </div>
  )

  if (!theme || !image) return (
    <div style={objStyles.page}>
      <header style={objStyles.header}>
        <button onClick={onBack} style={objStyles.backBtn}>← Temas</button>
      </header>
      <div style={objStyles.content}><p style={styles.errorText}>Objeto no encontrado.</p></div>
    </div>
  )

  const isCover = theme.coverUrl === image.url

  return (
    <div style={objStyles.page}>
      <header style={objStyles.header}>
        <button onClick={onBack} style={objStyles.backBtn}>← {theme.nameEs}</button>
        <h1 style={objStyles.headerTitle}>{image.wordEs}</h1>
        {image.wordEn && <span style={objStyles.headerSubtitle}>/ {image.wordEn}</span>}
        {theme.published ? (
          <span style={{ ...styles.statusBadge, ...styles.statusPublished }}>Publicado</span>
        ) : (
          <span style={{ ...styles.statusBadge, ...styles.statusDraft }}>Borrador</span>
        )}
      </header>

      <div style={objStyles.content}>
        {/* Image preview */}
        <div style={objStyles.previewCard}>
          <img src={image.url} alt={image.altEs} style={objStyles.previewImg} />
          {isCover && (
            <span style={{ ...styles.statusBadge, background: '#D4A017', color: '#fff' }}>Portada</span>
          )}
          {image.generationStatus === 'FAILED' && !dismissedImageError && (
            <div style={styles.errorBox}>
              <div style={styles.errorBoxHeader}>
                <span style={styles.errorBoxTitle}>Error de generación</span>
                <button onClick={dismissImageError} style={styles.errorBoxClose} aria-label="Cerrar error">
                  <FiX size={16} />
                </button>
              </div>
              <pre style={styles.errorBoxMessage}>{image.generationError ?? 'La generación falló sin un mensaje de error específico.'}</pre>
            </div>
          )}
        </div>

        {/* Edit form */}
        <div style={objStyles.formCard}>
          <h2 style={{ ...styles.sectionTitle, fontSize: '1.2rem' }}>Editar textos</h2>

          <div style={objStyles.formRow}>
            <div style={objStyles.formField}>
              <label style={objStyles.label}>Palabra ES *</label>
              <input style={styles.input} value={form.wordEs}
                onChange={(e) => setForm(f => ({ ...f, wordEs: e.target.value }))} />
            </div>
            <div style={objStyles.formField}>
              <label style={objStyles.label}>Palabra EN *</label>
              <input style={styles.input} value={form.wordEn}
                onChange={(e) => setForm(f => ({ ...f, wordEn: e.target.value }))} />
            </div>
          </div>

          <div style={objStyles.formRow}>
            <div style={objStyles.formField}>
              <label style={objStyles.label}>Alt ES *</label>
              <input style={styles.input} value={form.altEs}
                onChange={(e) => setForm(f => ({ ...f, altEs: e.target.value }))} />
            </div>
            <div style={objStyles.formField}>
              <label style={objStyles.label}>Alt EN *</label>
              <input style={styles.input} value={form.altEn}
                onChange={(e) => setForm(f => ({ ...f, altEn: e.target.value }))} />
            </div>
          </div>

          <div style={objStyles.formRow}>
            <div style={objStyles.formField}>
              <label style={objStyles.label}>Definición ES</label>
              <input style={styles.input} placeholder="(opcional)" value={form.definitionEs}
                onChange={(e) => setForm(f => ({ ...f, definitionEs: e.target.value }))} />
            </div>
            <div style={objStyles.formField}>
              <label style={objStyles.label}>Definición EN</label>
              <input style={styles.input} placeholder="(optional)" value={form.definitionEn}
                onChange={(e) => setForm(f => ({ ...f, definitionEn: e.target.value }))} />
            </div>
          </div>

          <div style={objStyles.formField}>
            <label style={objStyles.label}>Valor (céntimos)</label>
            <input style={{ ...styles.input, width: 120 }} type="number" min="0" placeholder="100 = 1€" value={form.value}
              onChange={(e) => setForm(f => ({ ...f, value: e.target.value }))} />
          </div>

          {saveError && <p style={styles.errorText}>{saveError}</p>}
          {saveSuccess && <p style={{ fontSize: '0.88rem', color: '#2D6A4F', fontWeight: 600, margin: 0 }}>Guardado ✓</p>}

          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={handleSave} disabled={saving} style={styles.submitBtn}>
              {saving ? 'Guardando…' : 'Guardar cambios'}
            </button>
          </div>
        </div>

        {/* Actions */}
        <div style={objStyles.actionsCard}>
          <h2 style={objStyles.actionsTitle}>Acciones</h2>

          {!isCover && (
            <button onClick={handleSetCover} style={{ ...objStyles.actionBtn, background: '#FEF3C7', color: '#92400E' }}>
              ⭐ Usar como portada
            </button>
          )}

          <div style={objStyles.actionRow}>
            <button onClick={() => { setShowReplace(v => !v); setShowRegen(false) }}
              style={{ ...objStyles.actionBtn, background: '#F0FAF4', color: styles.sectionTitle.color }}>
              <FiUpload size={14} /> Reemplazar imagen
            </button>
            <button onClick={() => { setShowRegen(v => !v); setShowReplace(false) }}
              style={{ ...objStyles.actionBtn, background: '#F5F3FF', color: '#5B21B6' }}>
              <FiRefreshCw size={14} /> Regenerar con IA
            </button>
          </div>

          {/* Replace panel */}
          {showReplace && (
            <div style={objStyles.replacePanel}>
              <p style={{ fontSize: '0.88rem', fontWeight: 700, color: '#2D6A4F', margin: 0 }}>
                Reemplazar imagen
              </p>
              <input
                ref={replaceFileRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                style={{ display: 'none' }}
                onChange={(e) => setReplaceFile(e.target.files?.[0] ?? null)}
              />
              <button
                type="button"
                onClick={() => replaceFileRef.current?.click()}
                style={styles.filePickBtn}
              >
                {replaceFile ? `✓ ${replaceFile.name}` : '📁 Seleccionar imagen'}
              </button>
              {replaceError && <p style={{ ...styles.errorText, margin: 0 }}>{replaceError}</p>}
              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={submitReplace} disabled={replaceUploading || !replaceFile} style={styles.submitBtn}>
                  {replaceUploading ? 'Subiendo…' : 'Subir'}
                </button>
                <button onClick={() => { setShowReplace(false); setReplaceFile(null); setReplaceError(null) }} style={styles.cancelBtn}>
                  Cancelar
                </button>
              </div>
            </div>
          )}

          {/* Regen panel */}
          {showRegen && (
            <div style={objStyles.regenPanel}>
              <p style={{ fontSize: '0.88rem', fontWeight: 700, color: '#5B21B6', margin: 0 }}>
                Regenerar con IA
              </p>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {(['image', 'texts', 'definition'] as const).map(type => (
                  <button
                    key={type}
                    onClick={() => setRegenType(type)}
                    style={{
                      ...objStyles.actionBtn,
                      background: regenType === type ? '#5B21B6' : '#F5F3FF',
                      color: regenType === type ? '#fff' : '#5B21B6',
                      fontSize: '0.875rem',
                      padding: '8px 14px',
                    }}
                  >
                    {type === 'image' ? 'Imagen' : type === 'texts' ? 'Textos' : 'Definición'}
                  </button>
                ))}
              </div>
              {regenType !== 'texts' && (
                <input
                  style={styles.input}
                  placeholder="Refinamiento (opcional)"
                  value={regenPrompt}
                  onChange={(e) => setRegenPrompt(e.target.value)}
                />
              )}
              {regenType === 'texts' && (
                <p style={{ fontSize: '0.875rem', color: '#888', margin: 0 }}>
                  Se generarán los textos a partir del nombre en español.
                </p>
              )}
              {regenError && <p style={{ ...styles.errorText, margin: 0 }}>{regenError}</p>}
              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={submitRegen} disabled={regenSubmitting} style={{ ...objStyles.actionBtn, background: '#5B21B6', color: '#fff' }}>
                  {regenSubmitting ? 'Enviando…' : '✨ Generar'}
                </button>
                <button onClick={() => { setShowRegen(false); setRegenPrompt(''); setRegenError(null) }} style={styles.cancelBtn}>
                  Cancelar
                </button>
              </div>
            </div>
          )}

          <div style={{ borderTop: `1px solid ${styles.input.borderColor}`, paddingTop: 12, marginTop: 4 }}>
            <button onClick={handleDelete} style={objStyles.dangerBtn}>
              <FiTrash2 size={14} /> Eliminar imagen
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
